import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../store';
import { streamUploadAndProcess } from '../../store/actions/BatchTables/BatchTablesActions';
import { hideSidePanel } from '../../store/actions/sidePanelActions';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import FileUploader from '../shared/TailwindComponents/FileUploader';
import Button from '../shared/TailwindComponents/Button';
import { PiUploadSimpleLight } from 'react-icons/pi';
import { MAX_UPLOAD_BYTES, MAX_UPLOAD_LABEL, partitionOversized, oversizedMessage } from '../../utils/uploadLimits';

interface AddTableDocumentPanelProps {
  tableUuid: string;
  onClose: () => void;
}

const AddTableDocumentPanel: React.FC<AddTableDocumentPanelProps> = ({ tableUuid, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [files, setFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  /*
    REFUSE AN OVERSIZED FILE HERE, with a reason.

    The uploader was handed `maxSizeBytes={Number.MAX_SAFE_INTEGER}` - no limit
    at all - so a file past the cap was accepted, batched (a file over the batch
    byte bound goes alone, by design), and then died at the edge as a protocol
    error with nothing readable to show. Batching cannot help: one file is one
    request however it is grouped. Every other uploader in the app partitions
    first; this one now does too.
  */
  const handleFilesChange = useCallback((newFiles: File[]) => {
    const { accepted, oversized } = partitionOversized(newFiles);
    if (oversized.length) {
      dispatch(showFeedbackModal({
        showModal: true,
        modalType: 'warning',
        message: t('Some files are too large to upload'),
        subMsg: oversizedMessage(oversized),
        duration: 8000,
      }));
    }
    setFiles(accepted);
  }, [dispatch, t]);

  const handleUpload = useCallback(() => {
    if (files.length === 0 || !tableUuid) return;

    // Kick off the streaming upload in the background and close the panel right
    // away. Documents appear on the table with loaders and process as they land;
    // an "Uploading X/Y" indicator on the detail screen tracks progress.
    setIsUploading(true);
    dispatch(streamUploadAndProcess(tableUuid, files) as any);
    dispatch(showFeedbackModal({
      showModal: true,
      message: t('Uploading {{count}} document(s)…', { count: files.length }),
      modalType: 'success',
      duration: 2500,
    }));
    dispatch(hideSidePanel());
    setIsUploading(false);
  }, [dispatch, files, tableUuid, t]);

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto p-6">
        <p className="text-sm text-fg-muted mb-2">
          {t('Select documents to upload to this table. Supported formats include PDF, DOCX, XLSX, and CSV.')}
        </p>
        <p className="text-xs text-fg-muted mb-4">
          {t('Maximum file size')}: {MAX_UPLOAD_LABEL}
        </p>
        <FileUploader
          files={files}
          onFilesChange={handleFilesChange}
          maxSizeBytes={MAX_UPLOAD_BYTES}
        />
      </div>

      <div className="glass-bar border-t border-hairline px-6 py-4 flex justify-end gap-3">
        <Button
          variant="outline"
          size="small"
          onClick={onClose}
          disabled={isUploading}
        >
          {t('Cancel')}
        </Button>
        <Button
          variant="primary"
          size="small"
          onClick={handleUpload}
          disabled={files.length === 0 || isUploading}
          icon={<PiUploadSimpleLight className="w-4 h-4" />}
        >
          {isUploading ? t('Uploading...') : t('Upload')}
        </Button>
      </div>
    </div>
  );
};

export default AddTableDocumentPanel;
